/**
 * The overlay editor (docs/ux/templates.md): a doctor's differences against
 * the base and the pack. Tick to keep a section, untick to leave it out; add
 * sections the base lacks; rename inline; standard wording per section; the
 * pack; the question sub-headings; clone from another doctor's live overlay.
 * The saved effective structure sits on the right and refreshes after Save.
 */

import { reportTypeLabel, type ReportTypeKind } from '@beam/core/status';
import { ChevronLeftIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import { toast } from 'sonner';
import { useOnline } from '@/app/online';
import type { TemplateOverlay, TemplateSection } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { ErrorPanel } from '@/components/shell/states';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useDoctor, useDoctors } from '../useRegistry';
import { ORIGIN_WORD, SOURCE_WORD, differenceWords, overlaySentence } from './describe';
import { problemsOf, useBase, useCloneOverlay, useOverlay, useResolved, useSaveOverlay, useTemplatesIndex, useVocabulary } from './useTemplates';

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function emptyOverlay(doctorSlug: string, reportType: ReportTypeKind, pack: string | null): TemplateOverlay {
  return { doctorSlug, reportType, pack, adds: [], removes: [], relabels: {}, order: null, standardWording: {}, questionSubHeadings: false, source: 'authored', clonedFrom: null, note: null };
}

export function OverlayEditorPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const online = useOnline();
  const { type = 'ime', slug = '' } = useParams();
  const reportType = type as ReportTypeKind;
  const doctor = useDoctor(slug);
  const base = useBase(reportType);
  const overlay = useOverlay(slug, reportType);
  const resolved = useResolved(slug, reportType);
  const vocabulary = useVocabulary();
  const index = useTemplatesIndex();
  const doctors = useDoctors();
  const save = useSaveOverlay(slug, reportType);
  const clone = useCloneOverlay(slug, reportType);
  const [draft, setDraft] = useState<TemplateOverlay | null>(null);
  const [changeNote, setChangeNote] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneFrom, setCloneFrom] = useState('');
  const [renaming, setRenaming] = useState<string | null>(null);
  const [addKey, setAddKey] = useState('');
  const problemsRef = useRef<HTMLDivElement>(null);

  const savedOverlay = overlay.data === undefined ? undefined : overlay.data?.version.body ?? null;
  const packDefault = resolved.data?.pack?.key ?? null;
  // The draft follows the saved overlay only; the resolved pack arrives from its own query and must never reset typed work.
  useEffect(() => {
    if (savedOverlay === undefined) return;
    setDraft(savedOverlay ?? emptyOverlay(slug, reportType, packDefault));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedOverlay, slug, reportType]);
  // A fresh, untouched overlay takes the resolved pack as its default when that lands.
  useEffect(() => {
    if (savedOverlay !== null || packDefault === null) return;
    setDraft((d) => (d && same(d, emptyOverlay(slug, reportType, null)) ? { ...d, pack: packDefault } : d));
  }, [packDefault, savedOverlay, slug, reportType]);
  useEffect(() => { if (problems.length > 0) problemsRef.current?.focus(); }, [problems]);

  const baseBody = base.data?.version.body;
  const packs = index.data?.packs ?? [];
  const dirty = draft !== null && !same(draft, savedOverlay ?? emptyOverlay(slug, reportType, packDefault));
  // Leaving the route with unsaved work asks first (the doctor page's pattern).
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const blocker = useBlocker(() => dirtyRef.current);
  const structureKeys = useMemo(() => {
    if (!baseBody) return new Set<string>();
    const keys = new Set(baseBody.sections.map((s) => s.key));
    return keys;
  }, [baseBody]);
  const available = useMemo(() => (vocabulary.data?.sections ?? []).filter((v) => !structureKeys.has(v.key) && !draft?.adds.some((a) => a.section.key === v.key)), [vocabulary.data, structureKeys, draft]);
  const cloneCandidates = (index.data?.overlays ?? []).filter((o) => o.reportType === reportType && o.doctorSlug !== slug);

  const title = `${doctor.data?.doctor.name ?? slug}: ${reportTypeLabel(reportType, 'label')}`;
  const back = <Link to={`/registry/doctors/${encodeURIComponent(slug)}?type=${reportType}`} className="inline-flex items-center gap-1 text-sm text-beam-text-muted hover:text-foreground"><ChevronLeftIcon aria-hidden="true" className="size-4" />{doctor.data?.doctor.name ?? 'Doctor'}</Link>;

  if (base.isError || overlay.isError || doctor.isError) return <div className="flex flex-col gap-3">{back}<PageHeader title={title} /><ErrorPanel message="The overlay could not be loaded." onRetry={() => { void base.refetch(); void overlay.refetch(); void doctor.refetch(); }} /></div>;
  if (!baseBody || !draft || !base.data) return <div className="flex flex-col gap-3">{back}<PageHeader title={title} /><Skeleton className="h-96 rounded-xl" aria-label="Loading the overlay" /></div>;

  const head = overlay.data?.head ?? null;
  function update(patch: Partial<TemplateOverlay>) { setDraft((d) => (d ? { ...d, ...patch } : d)); }
  function toggleKeep(key: string, keep: boolean) {
    update({ removes: keep ? draft!.removes.filter((k) => k !== key) : [...draft!.removes, key] });
  }
  function rename(key: string, label: string) {
    const relabels = { ...draft!.relabels };
    const original = baseBody!.sections.find((s) => s.key === key)?.label ?? draft!.adds.find((a) => a.section.key === key)?.section.label;
    if (!label.trim() || label.trim() === original) delete relabels[key]; else relabels[key] = label.trim();
    update({ relabels });
  }
  function wording(key: string, text: string) {
    const standardWording = { ...draft!.standardWording };
    if (text.trim()) standardWording[key] = text; else delete standardWording[key];
    update({ standardWording });
  }
  function add() {
    const v = vocabulary.data?.sections.find((x) => x.key === addKey);
    if (!v) return;
    const section: TemplateSection = { key: v.key, label: v.label, kind: v.kind, tier: 'doctor_specific', ...(v.guidance ? { guidance: v.guidance } : {}), ...(v.allowsNotes ? { allowsNotes: true } : {}) };
    update({ adds: [...draft!.adds, { section, after: null }] });
    setAddKey('');
  }
  async function onSave(confirm = false) {
    setProblems([]);
    try {
      const body: TemplateOverlay = confirm ? { ...draft!, source: 'confirmed' } : draft!.source === 'mined' ? draft! : { ...draft!, source: draft!.clonedFrom && !dirty ? draft!.source : draft!.source === 'cloned' && dirty ? 'authored' : draft!.source };
      const r = await save.mutateAsync({ body, changeNote: changeNote.trim() || (confirm ? 'Confirmed by the doctor.' : null) });
      toast.success(`Overlay version ${r.version.version} saved and live.`);
      setChangeNote('');
    } catch (err) {
      setProblems(problemsOf(err).problems);
    }
  }
  async function onClone() {
    setCloneOpen(false);
    if (!cloneFrom) return;
    try {
      const r = await clone.mutateAsync(cloneFrom);
      toast.success(`Cloned from ${doctors.data?.find((d) => d.slug === cloneFrom)?.name ?? cloneFrom}, version ${r.version.body.clonedFrom?.version ?? ''}.`.replace(', version .', '.'));
    } catch (err) {
      setProblems(problemsOf(err).problems);
    }
  }

  const removedCount = draft.removes.length;
  const baseVersion = base.data.head.liveVersion;

  return (
    <div className="flex flex-col gap-4">
      {back}
      <PageHeader
        title={title}
        meta={<span className="text-sm text-beam-text-muted" data-slot="overlay-version">{overlaySentence(head ? { version: head.liveVersion, source: draft.source } : null, baseVersion)}</span>}
        actions={phone ? undefined : (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" disabled={!online || cloneCandidates.length === 0} onClick={() => { setCloneFrom(''); setCloneOpen(true); }}>Clone from...</Button>
            <Button type="button" data-primary={dirty ? 'true' : undefined} disabled={!dirty || !online || save.isPending} aria-disabled={!dirty ? 'true' : undefined} title={!dirty ? 'Nothing has changed.' : undefined} onClick={() => void onSave()}>
              {save.isPending ? 'Saving' : `Save as version ${(head?.versions ?? 0) + 1}`}
            </Button>
          </div>
        )}
      />
      {phone ? <p className="text-sm text-beam-text-muted">Edit overlays from a desktop.</p> : null}
      <AlertDialog open={blocker.state === 'blocked'} onOpenChange={(o) => { if (!o && blocker.state === 'blocked') blocker.reset(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>The unsaved changes to this overlay are dropped; {head ? `version ${head.liveVersion} stays live` : 'the doctor keeps using the base'}.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (blocker.state === 'blocked') blocker.proceed(); }}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {!head ?<p className="text-sm text-beam-text-muted" data-slot="no-overlay">No overlay yet: this doctor uses the base. Saving creates version 1.</p> : null}
      {draft.source === 'mined' && head ? (
        <Alert data-slot="mined-alert">
          <AlertTitle>Mined from the corpus; a draft until confirmed.</AlertTitle>
          <AlertDescription className="flex flex-wrap items-center gap-3">{draft.note}{!phone ? <Button type="button" size="sm" variant="outline" disabled={!online || save.isPending} onClick={() => void onSave(true)}>Confirm as the doctor's</Button> : null}</AlertDescription>
        </Alert>
      ) : null}
      {problems.length > 0 ? (
        <Alert ref={problemsRef} tabIndex={-1} role="alert" variant="destructive" data-slot="template-problems">
          <AlertTitle>Not saved</AlertTitle>
          <AlertDescription><ul className="list-disc pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul></AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex flex-col gap-4">
          <section aria-label="Sections" className="rounded-xl border border-beam-line bg-beam-paper p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="eyebrow text-beam-text-muted">Sections (tick to keep, untick to leave out)</h2>
              <span className="text-xs text-beam-text-muted" data-slot="difference">{differenceWords({ added: draft.adds.length, removed: removedCount, renamed: Object.keys(draft.relabels).length })}</span>
            </div>
            <ul className="flex flex-col" data-slot="keep-list">
              {baseBody.sections.filter((s) => !s.parent).map((s) => {
                const kept = !draft.removes.includes(s.key);
                const label = draft.relabels[s.key];
                return (
                  <li key={s.key} data-slot="keep-row" data-key={s.key} data-kept={kept} className="flex flex-col gap-1 border-b border-beam-line py-2 text-sm last:border-b-0">
                    <div className="flex items-center gap-3">
                      <Checkbox id={`keep-${s.key}`} checked={kept} disabled={phone || !online || s.kind === 'questions'} aria-label={`Keep ${s.label}`} onCheckedChange={(v) => toggleKeep(s.key, v === true)} />
                      <Label htmlFor={`keep-${s.key}`} className={`min-w-0 flex-1 truncate font-normal ${kept ? '' : 'text-beam-text-muted line-through'}`}>
                        {label ?? s.label}{label ? <span className="ml-2 text-xs text-beam-text-muted no-underline">(was {s.label})</span> : null}
                      </Label>
                      <span className="text-xs text-beam-text-muted">{s.tier === 'core' ? 'core' : s.tier}</span>
                      {!phone && kept && s.kind !== 'questions' ? <Button type="button" variant="link" className="h-auto p-0 text-[13px]" disabled={!online} onClick={() => setRenaming(renaming === s.key ? null : s.key)}>{renaming === s.key ? 'Done' : 'Rename'}</Button> : null}
                    </div>
                    {renaming === s.key ? <Input aria-label={`Title for ${s.label}`} defaultValue={label ?? s.label} maxLength={80} autoFocus onBlur={(e) => rename(s.key, e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { rename(s.key, (e.target as HTMLInputElement).value); setRenaming(null); } }} /> : null}
                    {!phone && kept && (s.kind === 'prose' || s.kind === 'list') ? (
                      <Collapsible>
                        <CollapsibleTrigger className="text-xs text-beam-text-muted hover:text-foreground">{draft.standardWording[s.key] ? 'Standard wording set' : 'Standard wording'}</CollapsibleTrigger>
                        <CollapsibleContent><Textarea aria-label={`Standard wording for ${s.label}`} rows={3} maxLength={4000} defaultValue={draft.standardWording[s.key] ?? ''} onBlur={(e) => wording(s.key, e.target.value)} className="mt-1" /></CollapsibleContent>
                      </Collapsible>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </section>
          <section aria-label="Added by the doctor" className="rounded-xl border border-beam-line bg-beam-paper p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 className="eyebrow text-beam-text-muted">Added by the doctor</h2>
              {!phone ? (
                <div className="flex items-center gap-2">
                  <Select value={addKey} onValueChange={setAddKey}>
                    <SelectTrigger aria-label="Section to add" className="h-8 min-w-56 text-[13px]"><SelectValue placeholder="Add a section" /></SelectTrigger>
                    <SelectContent>{available.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" className="h-8" disabled={!addKey || !online} onClick={add}>Add</Button>
                </div>
              ) : null}
            </div>
            {draft.adds.length === 0 ? <p className="text-sm text-beam-text-muted">None.</p> : (
              <ul className="flex flex-col divide-y divide-beam-line text-sm" data-slot="added-list">
                {draft.adds.map((a) => (
                  <li key={a.section.key} data-key={a.section.key} className="flex flex-wrap items-center gap-3 py-2">
                    <span className="min-w-0 flex-1 font-medium">{draft.relabels[a.section.key] ?? a.section.label}</span>
                    <Select value={a.after ?? '__end'} onValueChange={(v) => update({ adds: draft.adds.map((x) => (x.section.key === a.section.key ? { ...x, after: v === '__end' ? null : v } : x)) })}>
                      <SelectTrigger aria-label={`Place ${a.section.label} after`} className="h-8 min-w-48 text-[13px]" disabled={phone || !online}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__end">Before the questions</SelectItem>
                        {baseBody.sections.filter((s) => !s.parent && !draft.removes.includes(s.key)).map((s) => <SelectItem key={s.key} value={s.key}>After {draft.relabels[s.key] ?? s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {!phone ? <Button type="button" variant="link" className="h-auto p-0 text-[13px] text-beam-error" disabled={!online} onClick={() => update({ adds: draft.adds.filter((x) => x.section.key !== a.section.key) })}>Remove</Button> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section aria-label="Options" className="grid gap-3 rounded-xl border border-beam-line bg-beam-paper p-4 text-sm md:grid-cols-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="pack">Specialty pack</Label>
              <Select value={draft.pack ?? '__none'} onValueChange={(v) => update({ pack: v === '__none' ? null : v })}>
                <SelectTrigger id="pack" className="h-9" disabled={phone || !online}><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__none">None</SelectItem>{packs.map((p) => <SelectItem key={p.head.id} value={p.head.id}>{p.head.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-3 md:justify-start">
              <Label htmlFor="sub-headings">The doctor's sub-headings under an answer</Label>
              <Switch id="sub-headings" checked={draft.questionSubHeadings} disabled={phone || !online} onCheckedChange={(v) => update({ questionSubHeadings: v })} />
            </div>
            {!phone ? (
              <div className="flex flex-col gap-1 md:col-span-2">
                <Label htmlFor="overlay-note">Change note</Label>
                <Textarea id="overlay-note" rows={2} maxLength={400} value={changeNote} placeholder="Say what changed and why" onChange={(e) => setChangeNote(e.target.value)} />
              </div>
            ) : null}
          </section>
        </div>
        <aside className="flex flex-col gap-4">
          <section aria-label="Effective structure" className="rounded-xl border border-beam-line bg-beam-paper p-4">
            <h2 className="eyebrow mb-2 text-beam-text-muted">Effective structure</h2>
            <p className="mb-2 text-xs text-beam-text-muted">{resolved.data ? `${resolved.data.sections.length} sections as saved${dirty ? '; the draft is unsaved' : ''}.` : 'Loading.'}</p>
            <ol className="flex flex-col text-[13px]" data-slot="effective-list">
              {(resolved.data?.sections ?? []).filter((s) => !s.parent).map((s, i) => (
                <li key={s.key} data-key={s.key} data-origin={s.origin} className="flex items-baseline gap-2 border-b border-beam-line py-1 last:border-b-0">
                  <span className="tabular w-5 text-right text-beam-text-muted">{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate">{s.label}</span>
                  <span className="text-xs text-beam-text-muted">{ORIGIN_WORD[s.origin]}{s.renamedFrom ? ', renamed' : ''}</span>
                </li>
              ))}
            </ol>
            {resolved.data?.openItems.map((o) => <p key={o} className="mt-2 text-xs text-beam-warning">{o}</p>)}
          </section>
          {head ? (
            <section aria-label="Versions" className="rounded-xl border border-beam-line bg-beam-paper p-4">
              <h2 className="eyebrow mb-2 text-beam-text-muted">Versions</h2>
              <ul className="flex flex-col divide-y divide-beam-line text-[13px]" data-slot="version-list">
                {[...(overlay.data?.versions ?? [])].reverse().map((v) => (
                  <li key={v.version} data-version={v.version} className="flex flex-col gap-0.5 py-2">
                    <span className="font-medium tabular">Version {v.version}{v.version === head.liveVersion ? <span className="ml-2 rounded-full bg-beam-cream-deep px-2 py-0.5 text-xs font-normal" data-slot="current">current</span> : null}</span>
                    <span className="text-beam-text-muted">{v.createdByName ?? ''}{v.changeNote ? `: ${v.changeNote}` : ''}</span>
                  </li>
                ))}
              </ul>
              {draft.clonedFrom ? <p className="mt-2 text-xs text-beam-text-muted">Cloned from {doctors.data?.find((d) => d.slug === draft.clonedFrom?.doctorSlug)?.name ?? draft.clonedFrom.doctorSlug}, version {draft.clonedFrom.version} ({SOURCE_WORD[draft.source]}).</p> : null}
            </section>
          ) : null}
        </aside>
      </div>

      <AlertDialog open={cloneOpen} onOpenChange={setCloneOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clone another doctor's {reportTypeLabel(reportType, 'short')} overlay?</AlertDialogTitle>
            <AlertDialogDescription>{head ? 'The clone becomes the next live version; the earlier versions stay in the list.' : 'The clone becomes version 1 of this overlay.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <Select value={cloneFrom} onValueChange={setCloneFrom}>
            <SelectTrigger aria-label="Doctor to clone from" className="h-9"><SelectValue placeholder="Choose the doctor" /></SelectTrigger>
            <SelectContent>{cloneCandidates.map((o) => <SelectItem key={o.doctorSlug} value={o.doctorSlug}>{doctors.data?.find((d) => d.slug === o.doctorSlug)?.name ?? o.doctorSlug} (version {o.head.liveVersion}, {SOURCE_WORD[o.source]})</SelectItem>)}</SelectContent>
          </Select>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep as is</AlertDialogCancel>
            <AlertDialogAction disabled={!cloneFrom} onClick={(e) => { e.preventDefault(); void onClone(); }}>Clone</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
